import { useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Container, Note, TextLink } from '../ui';
import { profile } from '../../data/profile';
import WhatsAppWidget from '../contact/WhatsAppWidget';

// Route changes scroll to the top, or to the #hash target when there is one.
function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      document.getElementById(hash.slice(1))?.scrollIntoView();
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }, [pathname, hash]);
  return null;
}

const nav = [
  { to: '/#work', label: 'Work' },
  { to: '/#experience', label: 'Experience' },
  { to: '/#contact', label: 'Contact' },
];

// Same shapes as public/favicon.svg: lines of text, one of them highlighted.
function Logomark() {
  return (
    <svg viewBox="0 0 64 64" width="32" height="32" aria-hidden="true" focusable="false">
      <rect width="64" height="64" fill="var(--color-ink)" />
      <rect x="11" y="9" width="42" height="6" fill="var(--color-paper)" />
      <rect x="9" y="25" width="46" height="14" fill="var(--color-paper)" />
      <rect x="11" y="28" width="38" height="6" fill="var(--color-ink)" />
      <rect x="11" y="49" width="26" height="6" fill="var(--color-paper)" />
    </svg>
  );
}

function SiteHeader() {
  const { pathname } = useLocation();
  const onHome = pathname === '/';
  return (
    <header>
      <Container className="flex flex-wrap items-center justify-between gap-x-8 gap-y-2 py-6">
        {/* On Home the name is the page title, so the header shows only the mark. */}
        <Link
          to="/"
          aria-label={`${profile.name}, home`}
          className="flex items-center gap-3 font-display text-h3 focus-visible:bg-mark focus-visible:text-on-mark"
        >
          <Logomark />
          {!onHome && <span>{profile.name}</span>}
        </Link>
        <nav aria-label="Main">
          <ul className="flex gap-6 font-display font-medium">
            {nav.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className="py-2 transition-colors duration-fast hover:bg-mark hover:text-on-mark focus-visible:bg-mark focus-visible:text-on-mark"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </Container>
    </header>
  );
}

function SiteFooter() {
  return (
    <footer className="border-t border-rule">
      <Container className="flex flex-wrap items-center justify-between gap-4 py-8">
        <Note>
          © {new Date().getFullYear()} {profile.name}
        </Note>
        <TextLink to="/" className="font-display font-medium">
          Portfolio home
        </TextLink>
      </Container>
    </footer>
  );
}

export default function Layout() {
  return (
    <>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:bg-mark focus:text-on-mark focus:px-3 focus:py-2 focus:font-display focus:font-semibold"
      >
        Skip to content
      </a>
      <ScrollManager />
      <SiteHeader />
      <main id="main" tabIndex={-1} className="outline-none">
        <Outlet />
      </main>
      <SiteFooter />
      <WhatsAppWidget />
    </>
  );
}
